import { Check, CreditCard, Zap, Palette } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { MarketingFooter } from '#app/components/marketing-footer.tsx'
import { MarketingHeader } from '#app/components/marketing-header.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Switch } from '#app/components/ui/switch.tsx'
import { cn } from '#app/utils/misc.tsx'

export default function Pricing() {
  const { t } = useTranslation(['marketing', 'common'])
  const [isYearly, setIsYearly] = useState(false)
  
  const plans = [
    {
      name: t('marketing:pricing.plans.free.name'),
      price: t('marketing:pricing.plans.free.price'),
      period: t('marketing:pricing.plans.free.period'),
      description: t('marketing:pricing.plans.free.description'),
      features: t('marketing:pricing.plans.free.features', { returnObjects: true }) as string[],
      cta: t('marketing:pricing.plans.free.cta'),
      variant: 'outline' as const,
      popular: false
    },
    {
      name: t('marketing:pricing.plans.pro.name'),
      price: isYearly ? t('marketing:pricing.plans.pro.priceYearly') : t('marketing:pricing.plans.pro.price'),
      period: t('marketing:pricing.plans.pro.period'),
      description: t('marketing:pricing.plans.pro.description'),
      features: t('marketing:pricing.plans.pro.features', { returnObjects: true }) as string[],
      cta: t('marketing:pricing.plans.pro.cta'),
      variant: 'default' as const,
      popular: true
    },
    {
      name: t('marketing:pricing.plans.ultra.name'),
      price: isYearly ? t('marketing:pricing.plans.ultra.priceYearly') : t('marketing:pricing.plans.ultra.price'),
      period: t('marketing:pricing.plans.ultra.period'),
      description: t('marketing:pricing.plans.ultra.description'),
      features: t('marketing:pricing.plans.ultra.features', { returnObjects: true }) as string[],
      cta: t('marketing:pricing.plans.ultra.cta'),
      variant: 'outline' as const,
      popular: false
    }
  ]

  return (
    <div className="flex flex-col min-h-screen">
      <MarketingHeader />

      <main className="flex-1">
        {/* Pricing Section */}
        <section className="py-20">
          <div className="container">
            <div className="text-center mb-12 space-y-4">
              <h1 className="text-4xl font-bold font-[family-name:var(--font-heading)]">{t('marketing:pricing.title')}</h1>
              <p className="text-xl text-muted-foreground">
                {t('marketing:pricing.subtitle')}
              </p>
              
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <Label htmlFor="billing-toggle" className={cn(!isYearly && "text-primary font-semibold")}>
                  {t('marketing:pricing.monthly')}
                </Label>
                <Switch
                  id="billing-toggle"
                  checked={isYearly}
                  onCheckedChange={setIsYearly}
                />
                <Label htmlFor="billing-toggle" className={cn("flex items-center gap-2", isYearly && "text-primary font-semibold")}>
                  {t('marketing:pricing.yearly')}
                  <span className="text-sm bg-primary/10 text-primary px-2 py-1 rounded">{t('marketing:pricing.save')}</span>
                </Label>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={cn(
                    "relative rounded-lg border p-8",
                    plan.popular && "border-primary shadow-lg scale-105"
                  )}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-0 right-0 flex justify-center">
                      <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded-full">
                        {t('marketing:pricing.mostPopular')}
                      </span>
                    </div>
                  )}
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold font-[family-name:var(--font-heading)]">{plan.name}</h3>
                      <p className="text-muted-foreground mt-2">{plan.description}</p>
                    </div>
                    
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                    
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span className={cn(
                            "text-sm",
                            feature.startsWith('Alles in') && "font-semibold"
                          )}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link to="/signup">
                      <Button className="w-full" variant={plan.variant}>
                        {plan.cta}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pay as you go */}
            <div className="mt-16 max-w-3xl mx-auto">
              <div className="bg-muted rounded-lg p-8 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold font-[family-name:var(--font-heading)]">{t('marketing:pricing.payAsYouGo.title')}</h3>
                <p className="text-muted-foreground">
                  {t('marketing:pricing.payAsYouGo.description')}
                </p>
                <div className="text-3xl font-bold">{t('marketing:pricing.payAsYouGo.price')}</div>
                <p className="text-sm text-muted-foreground">
                  {t('marketing:pricing.payAsYouGo.details')}
                </p>
                <div className="flex items-center justify-center gap-4 pt-4">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t('marketing:pricing.payAsYouGo.payment')}
                  </span>
                </div>
              </div>
            </div>

            {/* Branding Addon */}
            <div className="mt-16 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-8 border border-primary/20">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                      <Palette className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold font-[family-name:var(--font-heading)]">
                      {t('marketing:pricing.brandingAddon.title')}
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      {t('marketing:pricing.brandingAddon.description')}
                    </p>
                    {/* /contact does not exist — route branding requests to support */}
                    <Link to="/support">
                      <Button className="mt-4">
                        {t('marketing:pricing.brandingAddon.cta')}
                      </Button>
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {(t('marketing:pricing.brandingAddon.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}